import * as XLSX from "xlsx";

import type { PriceType } from "../clients/usePricelists";

// what sheet_to_json yields per cell with raw:false
type CellValue = string | number | boolean | Date | null | undefined;

export interface ParsedPriceRow {
    identifier: string; // column A — manufacturer identifier value
    price: {
        basicUnitOfMeasurement?: string;
        calculationUnitOfMeasurement?: string;
        unitOfIssue?: string;
        roundToPiece?: number;
        roundToPackage?: number;
        roundToPalette?: number;
        disablePackageRounding?: boolean;
        vat?: number;
        valid?: { from?: string; to?: string };
        type: PriceType;
        value: number;
    };
}

// Public URL of the canonical manufacturer price-list template (same for all
// brands). Hosted on public storage; replace the file there to update it.
export const TEMPLATE_URL
    = "https://data2.bimproject.cz/templates/Pricelist-template-manufacturer.xlsx";

// Manufacturer price-list template (v2) column layout, 0-based.
// A = manufacturer identifier (EAN/GTIN/Code/Article number — resolved to a
// product UUID via product-search). B = informative name (ignored).
// C (discount group) is deferred — no discount-group entity yet.
// !WARNING! template might change in the future
// by now fields are hardcoded
const COL = {
    key: 0, // A — manufacturer identifier value
    name: 1, // B — informative, ignored
    discountGroup: 2, // C — deferred
    basicUoM: 3, // D — basic UoM (what we sell)
    calculationUoM: 4, // E — UoM (what we calculate)
    value: 5, // F — cost per UoM
    vat: 6, // G — VAT [%]
    packageType: 7, // H — type of package (unitOfIssue)
    disableRounding: 8, // I — disable rounding of packages
    roundToPiece: 9, // J — rounding UoM to pieces
    roundToPackage: 10, // K — rounding UoM to packages
    roundToPalette: 11, // L — rounding UoM to palettes
    validFrom: 12, // M — validity start
    validTo: 13, // N — validity end
} as const;

function str(v: CellValue): string | undefined {
    const s = v?.toString().trim();
    return s || undefined;
}

function num(v: CellValue): number | undefined {
    const n = parseFloat(String(v));
    return isFinite(n) ? n : undefined;
}

function bool(v: CellValue): boolean | undefined {
    const s = v?.toString().trim().toLowerCase();
    if (!s) return undefined;
    return ["1", "true", "yes", "y", "x", "ano"].includes(s);
}

// Cells come through as formatted strings (raw:false). A validity cell may still
// arrive as an Excel serial number — convert those to an ISO (yyyy-mm-dd) date.
function dateStr(v: CellValue): string | undefined {
    const s = v?.toString().trim();
    if (!s) return undefined;
    if (/^\d+(\.\d+)?$/.test(s)) {
        const serial = parseFloat(s);
        const ms = Math.round((serial - 25569) * 86400 * 1000);
        return new Date(ms).toISOString().slice(0, 10);
    }
    return s;
}

export async function parseXlsx(file: File): Promise<CellValue[][]> {
    const buffer = await file.arrayBuffer();
    const wb = XLSX.read(buffer, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<CellValue[]>(ws, {
        header: 1,
        raw: false,
    });
    return rows.slice(1); // skip header
}

// Parse rows into { identifier, price } pairs. Rows missing an identifier or a
// valid price are counted as failed and skipped.
export function parsePriceRows(rows: CellValue[][]): {
    rows: ParsedPriceRow[];
    failed: number;
} {
    const parsed: ParsedPriceRow[] = [];
    let failed = 0;

    for (const row of rows) {
        const identifier = str(row[COL.key]);
        const value = num(row[COL.value]);

        if (!identifier || value === undefined) {
            failed++;
            continue;
        }

        const unitOfIssue = str(row[COL.packageType]);
        const validFrom = dateStr(row[COL.validFrom]);
        const validTo = dateStr(row[COL.validTo]);

        parsed.push({
            identifier,
            price: {
                basicUnitOfMeasurement: str(row[COL.basicUoM]),
                calculationUnitOfMeasurement: str(row[COL.calculationUoM]),
                unitOfIssue,
                roundToPiece: num(row[COL.roundToPiece]),
                roundToPackage: num(row[COL.roundToPackage]),
                roundToPalette: num(row[COL.roundToPalette]),
                disablePackageRounding: bool(row[COL.disableRounding]),
                vat: num(row[COL.vat]),
                valid:
                    validFrom || validTo
                        ? { from: validFrom, to: validTo }
                        : undefined,
                type: (unitOfIssue ? "ISSUE" : "UNIT") as PriceType,
                value,
            },
        });
    }

    return { rows: parsed, failed };
}

// Assemble PriceInputs from parsed rows using a resolved identifier -> UUID map.
// Rows whose identifier didn't resolve are returned separately as notFound.
export function assemblePrices(
    pricelistID: string,
    rows: ParsedPriceRow[],
    idByValue: Map<string, string>,
) {
    const prices = [];
    const notFound: string[] = [];

    for (const { identifier, price } of rows) {
        const id = idByValue.get(identifier);
        if (!id) {
            notFound.push(identifier);
            continue;
        }
        prices.push({ pricelistID, product: { id }, ...price });
    }

    return { prices, notFound };
}
